/**
 * <ry-field>
 *
 * Form field wrapper with label, error, and hint.
 *
 * Usage:
 * <ry-field label="Email">
 *   <input type="email" placeholder="you@example.com">
 * </ry-field>
 *
 * <ry-field label="Password" hint="Min 8 characters" error="Too short">
 *   <input type="password">
 * </ry-field>
 *
 * JS uses data-ry-target for queries, CSS uses classes for styling.
 */

import { RyElement } from '../core/ry-element.js';

export class RyField extends RyElement {
  static get observedAttributes(): string[] {
    return ['label', 'error', 'hint'];
  }

  setup(): void {
    this.#transformMarkup();
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;

    if (name === 'error') {
      this.#updateError(newValue);
    } else if (name === 'hint') {
      this.#updateHint(newValue);
    } else if (name === 'label') {
      this.#updateLabel(newValue);
    }
  }

  #transformMarkup(): void {
    const input = this.$<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>('input, textarea, select');
    if (!input) return;

    // Generate ID if needed
    const inputId = input.id || `ry-field-${Math.random().toString(36).slice(2, 9)}`;
    input.id = inputId;

    // Label
    if (!this.$('[data-ry-target="label"]')) {
      const labelText = this.getAttribute('label');
      if (labelText) {
        const label = document.createElement('label');
        label.setAttribute('data-ry-target', 'label');
        label.className = 'ry-label';
        label.setAttribute('for', inputId);
        label.textContent = labelText;
        this.insertBefore(label, input);
      }
    }

    // Error message (hidden by default via CSS when empty)
    if (!this.$('[data-ry-target="error"]')) {
      const errorEl = document.createElement('div');
      errorEl.setAttribute('data-ry-target', 'error');
      errorEl.className = 'ry-field__error';
      errorEl.setAttribute('role', 'alert');
      const errorText = this.getAttribute('error');
      if (errorText) {
        errorEl.textContent = errorText;
      }
      input.insertAdjacentElement('afterend', errorEl);
    }

    // Hint text (hidden when error is visible)
    if (!this.$('[data-ry-target="hint"]')) {
      const hintEl = document.createElement('div');
      hintEl.setAttribute('data-ry-target', 'hint');
      hintEl.className = 'ry-field__hint';
      const hintText = this.getAttribute('hint');
      if (hintText) {
        hintEl.textContent = hintText;
      }
      const errorEl = this.$('[data-ry-target="error"]');
      if (errorEl) {
        errorEl.insertAdjacentElement('afterend', hintEl);
      } else {
        input.insertAdjacentElement('afterend', hintEl);
      }
    }

    // Link input to error via aria-describedby
    const errorEl = this.$('[data-ry-target="error"]');
    const hintEl = this.$('[data-ry-target="hint"]');
    const describedBy: string[] = [];
    if (errorEl) {
      errorEl.id = `${inputId}-error`;
      describedBy.push(errorEl.id);
    }
    if (hintEl) {
      hintEl.id = `${inputId}-hint`;
      describedBy.push(hintEl.id);
    }
    if (describedBy.length) {
      input.setAttribute('aria-describedby', describedBy.join(' '));
    }
  }

  #updateError(text: string | null): void {
    const el = this.$('[data-ry-target="error"]');
    if (el) {
      el.textContent = text ?? '';
    }
    // Toggle invalid state on the input
    const input = this.$<HTMLInputElement>('input, textarea, select');
    if (input) {
      if (text) {
        input.setAttribute('aria-invalid', 'true');
      } else {
        input.removeAttribute('aria-invalid');
      }
    }
  }

  #updateHint(text: string | null): void {
    const el = this.$('[data-ry-target="hint"]');
    if (el) {
      el.textContent = text ?? '';
    }
  }

  #updateLabel(text: string | null): void {
    const el = this.$('[data-ry-target="label"]');
    if (el) {
      el.textContent = text ?? '';
    }
  }
}

customElements.define('ry-field', RyField);
